import { GetCustomersParams } from "@/models/payload/customer";
import customerService from "@/services/customer";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

type Props = {
  term: string;
  count: number;
  skip: number;
  enabled: boolean;
};

function useGetCustomers({ term, count, skip, enabled }: Props) {
  const {
    data,
    isLoading,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["customers", term, count, skip],
    queryFn: ({ pageParam = 0 }) =>
      customerService.getCustomers({
        Skip: pageParam,
        Count: count,
        Term: term,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const morePagesExist = lastPage?.result?.length === count; // Assuming 10 items per page
      if (!morePagesExist) return undefined;
      return allPages.length * (count || 10); // Next page's skip value
    },
    enabled: enabled,
  });

  return {
    data,
    isLoading,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  };
}

function useGetCustomerPagination(params: GetCustomersParams) {
  return useQuery(
    ["customersPagination", params?.Skip, params?.Count, params?.Term],
    () => customerService.getCustomers({ ...params, Skip: params.Skip }),
  );
}

export default useGetCustomers;

export { useGetCustomerPagination };
